import { DEFAULT_TAG } from '../Model/AppCustomCheckin';

/**
 * Which check-in template a client receives from one coach.
 *
 * Mobile resolves with the same rule (`resolveCheckInTemplateTag` in system2-mobile-react-native
 * `utils/checkIn.ts`); keep the two and their test tables in step.
 *
 * A template is a candidate when the client carries its tag and it has at least one question.
 *   1. The coach's selection wins when it is `default` or a candidate.
 *   2. Otherwise - nothing selected, or the selected tag is no longer a candidate - a single
 *      candidate applies.
 *   3. With several candidates and no valid selection the alphabetically first applies, the order
 *      clients got before the selection existed, so nothing changes until the coach picks.
 *   4. With no candidate the coach's default applies.
 *
 * @param {object} params
 * @param {string} [params.selectedTag] - The coach's selection, if any.
 * @param {string[]} params.clientTags - The client's tags for this coach.
 * @param {string[]} params.templateTags - Tags of this coach's templates that have questions.
 * @return {{ tag: string, candidates: string[], isChosen: boolean, isTagNameFallback: boolean }} `isChosen` when
 *   the coach's selection applies; `isTagNameFallback` when several candidates and no valid selection left the
 *   alphabetically first to apply.
 */
const resolveCheckInTemplateTag = ({ selectedTag, clientTags, templateTags }) => {
  const usable = new Set(templateTags);
  const candidates = [...new Set(clientTags)].filter((tag) => usable.has(tag)).sort();

  if (selectedTag === DEFAULT_TAG || candidates.includes(selectedTag)) {
    return {
      tag: selectedTag, candidates, isChosen: true, isTagNameFallback: false,
    };
  }
  if (candidates.length === 0) {
    return {
      tag: DEFAULT_TAG, candidates, isChosen: false, isTagNameFallback: false,
    };
  }
  return {
    tag: candidates[0], candidates, isChosen: false, isTagNameFallback: candidates.length > 1,
  };
};

export { resolveCheckInTemplateTag };
